import { getChartLabel } from "./chartLabel";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Form } from "react-bootstrap";
import CustomLegend from "./CustomLegend";
import CustomTooltip from "./CustomTooltip";

const ServicesChart = ({ data, filters, selectedState }) => {
  const [chartDataByService, setChartDataByService] = useState({});
  const [showTable, setShowTable] = useState(false);

  const descriptions = [
    "Number of facilities",
    "Number of facilities which have submitted monthly report",
    "Percentage of facility reports received",
  ];

  const services = [
    "ti",
    "lws",
    "ost_center",
    "prisons",
    "ocs",
    "one_stop_centre",
    "ictc",
    "ssk",
    "dsrc",
    "art_centre",
    "link_art_centre",
    "vl_lab",
  ];

  const formatServiceName = (service) => {
    return service
      .replace(/_/g, " ") // replace underscores with space
      .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize first letter of each word
  };

  const places = Array.from(
    new Set(
      Object.values(chartDataByService)
        .flat()
        .map((entry) => entry.Place),
    ),
  );

  const descriptionTitles = {
    "Number of facilities": "Number of Facilities",
    "Number of facilities which have submitted monthly report":
      "Number of facilities which have submitted monthly report",
    "Percentage of facility reports received":
      "Percentage of facility reports received",
  };

  const descriptionKeys = Object.keys(descriptionTitles);

  useEffect(() => {
    const organizedData = {};

    services.forEach((service) => {
      const chartData = [];

      if (!Array.isArray(data)) {
        console.warn("Invalid data format, expected an array");
        return;
      }

      const places = [...new Set(data.map((entry) => entry.Place))];

      places.forEach((place) => {
        const entry = { Place: place };

        descriptions.forEach((desc) => {
          const item = data.find((row) => {
            if (
              !row ||
              typeof row.Place !== "string" ||
              typeof row.description !== "string"
            ) {
              console.warn("Skipping row due to missing data:", row);
              return false;
            }

            return (
              row.Place.trim().toLowerCase() === place.trim().toLowerCase() &&
              row.description.trim().toLowerCase() === desc.trim().toLowerCase()
            );
          });

          const value = item && item[service];
          entry[desc] = value && !isNaN(value) ? parseFloat(value) : 0;
        });

        chartData.push(entry);
      });

      organizedData[service] = chartData;
    });

    setChartDataByService(organizedData);
  }, [data]);
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <h5 style={{ color: "#666", fontSize: "20px" }}>No data available</h5>
      </div>
    );
  }

  const chunkArray = (array, chunkCount) => {
    const result = [];
    const chunkSize = Math.ceil(array.length / chunkCount);

    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }

    return result;
  };

  const getChartCount = (length) => {
    if (length < 9) return 1;
    if (length >= 9 && length <= 18) return 2;
    if (length <= 45) return 3;
    return Math.ceil(length / 20);
  };

  return (
    <div>
      {/* Toggle switch at top right */}
      <div className="card hptCard mt-4 border-0">
        <div class="card-header brBtomlr">
          <div class="headnumText justify-content-between">
            <div class="cardTitle">
              <h5>Reporting Status of NACP Facilities {getChartLabel(filters, selectedState)}</h5>
            </div>

            <div class="cardDropDown gap-3">
              <div style={{ marginRight: "10px", fontWeight: 500 }}>
                {showTable ? "Chart View" : "Table View"}
              </div>
              <Form>
                <Form.Check
                  type="switch"
                  id="chart-table-toggle"
                  onChange={() => setShowTable((prev) => !prev)}
                  checked={showTable}
                />
              </Form>
            </div>
          </div>
        </div>
      </div>
      {showTable ? (
        <div>
          {descriptionKeys.map((description, descIndex) => (
            <>
              <div className="card hptCard mt-4 border-0" key={descIndex}>
                {/* style={{ marginBottom: "40px" }} */}
                <div className="card-body">
                  <h6 style={{ fontWeight: "bold" }}>
                    {descriptionTitles[description]}
                  </h6>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered ">
                    <thead className="thead-light">
                      <tr>
                        <th className="fw-bold">Place</th>
                        {services.map((service) => (
                          <th key={service} className="text-center fw-bold">
                            {formatServiceName(service).toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {places.map((place, rowIdx) => (
                        <tr key={rowIdx}>
                          <td className="fw-bold">{place}</td>
                          {services.map((service, colIdx) => {
                            const entry = chartDataByService[service]?.find(
                              (row) => row.Place === place,
                            );
                            const value = entry ? entry[description] : "-";
                            return (
                              <td key={colIdx} className="text-center ">
                                {/* {entry ? entry[description] : "-"} */}
                                {description ===
                                  "Percentage of facility reports received" &&
                                value !== "-"
                                  ? `${value}%`
                                  : value}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ))}
        </div>
      ) : (
        // Existing Chart View Code
        Object.entries(chartDataByService).map(([serviceName, chartData]) => {
          const chartCount = getChartCount(chartData.length);
          const chunkedData = chunkArray(chartData, chartCount);

          return (
            <div>
              {chunkedData.map((chunk, idx) => (
                <div className="card hptCard mt-4 border-0" key={serviceName}>
                  <div className="card-body">
                    <h6 style={{ fontWeight: "bold" }}>
                      {formatServiceName(serviceName).toUpperCase()}
                    </h6>
                  </div>
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                      data={chunk}
                      width={500}
                      height={500}
                      padding={{
                        top: 30,
                      }}
                      margin={{
                        top: 30,
                        right: 30,
                        left: 0,
                        bottom: 130,
                      }}
                    >
                      {/* <CartesianGrid strokeDasharray="3 3" /> */}
                      <XAxis
                        dataKey="Place"
                        angle={-90}
                        textAnchor="end"
                        interval={0}
                        label={{
                          value: "",
                          position: "insideBottom",
                          offset: -110,
                          color: "#dd1e47",
                          fontWeight: "bold",
                        }}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="top"
                        content={
                          <CustomLegend
                            order={[
                              "Number of facilities",
                              "Number of facilities which have submitted monthly report",
                            ]}
                          />
                        }
                      />
                      <Bar dataKey="Number of facilities" fill="#00a084">
                        <LabelList
                          dataKey="Number of facilities"
                          position="top"
                          content={({ x, y, width, value }) => {
                            if (value == null) return null;
                            return (
                              <text
                                x={x + width / 2} // center horizontally
                                y={y - 5} // position slightly above bar
                                fontSize={12}
                                fontWeight="bold"
                                textAnchor="start" // 👈 could also try "middle" for alignment
                                transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                              >
                                {value}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                      <Bar
                        dataKey="Number of facilities which have submitted monthly report"
                        fill="#ffc40c"
                      >
                        <LabelList
                          dataKey="Number of facilities which have submitted monthly report"
                          position="top"
                          content={({ x, y, width, value }) => {
                            if (value == null) return null;
                            return (
                              <text
                                x={x + width / 2} // center horizontally
                                y={y - 5} // position slightly above bar
                                fontSize={12}
                                fontWeight="bold"
                                textAnchor="start" // 👈 could also try "middle" for alignment
                                transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                              >
                                {value}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                      data={chunk}
                      width={500}
                      height={500}
                      margin={{
                        top: 50,
                        right: 30,
                        left: 0,
                        bottom: 130,
                      }}
                    >
                      {/* <CartesianGrid strokeDasharray="3 3" /> */}
                      <XAxis
                        dataKey="Place"
                        angle={-90}
                        textAnchor="end"
                        interval={0}
                        label={{
                          value: "",
                          position: "insideBottom",
                          offset: -110,
                          color: "#dd1e47",
                          fontWeight: "bold",
                        }}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="top"
                        content={
                          <CustomLegend
                            order={["Percentage of facility reports received"]}
                          />
                        }
                      />
                      <Bar
                        dataKey="Percentage of facility reports received"
                        fill="#dd1e47"
                      >
                        <LabelList
                          dataKey="Percentage of facility reports received"
                          position="top"
                          content={({ x, y, width, value }) => {
                            if (value == null) return null;
                            return (
                              <text
                                x={x + width / 2} // center horizontally
                                y={y - 5} // position slightly above bar
                                fontSize={12}
                                fontWeight="bold"
                                textAnchor="start" // 👈 could also try "middle" for alignment
                                transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                              >
                                {value}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
};

export default ServicesChart;

// // ServicesChart.js-kpi 4
// import React, { useEffect, useState } from "react";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   Legend,
//   CartesianGrid,
//   ResponsiveContainer,
// } from "recharts";

// const ServicesChart = ({ data, filters, selectedState }) => {
//   const [chartDataByService, setChartDataByService] = useState({});
//   const descriptions = [
//     "Number of facilities",
//     "Number of facilities which have submitted monthly report",
//     "Percentage of facility reports received",
//   ];

//   const services = [
//     "ti",
//     "lws",
//     "ost_center",
//     "prisons",
//     "ocs",
//     "one_stop_centre",
//     "ictc",
//     "ssk",
//     "dsrc",
//     "art_centre",
//     "link_art_centre",
//     "vl_lab",
//   ];

//   useEffect(() => {
//     const organizedData = {};

//     services.forEach((service) => {
//       const chartData = [];

//       if (!Array.isArray(data)) {
//         console.warn("Invalid data format, expected an array");
//         return;
//       }

//       const places = [...new Set(data.map((entry) => entry.Place))];

//       places.forEach((place) => {
//         const entry = { Place: place };

//         descriptions.forEach((desc) => {
//           const item = data.find((row) => {
//             if (
//               !row ||
//               typeof row.Place !== "string" ||
//               typeof row.description !== "string"
//             ) {
//               console.warn("Skipping row due to missing data:", row);
//               return false;
//             }

//             const match =
//               row.Place.trim().toLowerCase() === place.trim().toLowerCase() &&
//               row.description.trim().toLowerCase() ===
//                 desc.trim().toLowerCase();

//             if (match) {
//               console.log(`✅ Found match for ${place} ${desc}`);
//             }

//             return match;
//           });

//           if (!item) {
//             console.warn(
//               `❌ Not found for Place: "${place}", Description: "${desc}"`
//             );
//           }

//           const value = item && item[service];
//           entry[desc] = value && !isNaN(value) ? parseFloat(value) : 0;
//         });

//         chartData.push(entry);
//       });

//       organizedData[service] = chartData;
//     });

//     setChartDataByService(organizedData);
//   }, [data]);

//   return (
//     <div>
//       {Object.entries(chartDataByService).map(([serviceName, chartData]) => (
//         <div key={serviceName} style={{ marginBottom: "40px" }}>
//           <h6>{serviceName.toUpperCase()}</h6>
//           <ResponsiveContainer width="100%" height={300}>
//             <BarChart data={chartData}>
//               <CartesianGrid strokeDasharray="3 3" />
//               <XAxis dataKey="Place" />
//               <YAxis />
//               <Tooltip
//               contentStyle={{
//                 backgroundColor: "#f5f5f5",
//                 borderRadius: "8px",
//                 border: "1px solid #ccc",
//                 fontSize: "14px",
//                 color: "#ca8282ff",
//                 fontWeight: "bold",
//               }}
//               itemStyle={{
//                 color: "#333",
//                 fontWeight: "bold",
//               }}
//               cursor={{ fill: "none" }}
//               />
//               <Legend />
//               {descriptions.map((desc, index) => (
//                 <Bar
//                   key={index}
//                   dataKey={desc}
//                   fill={["#8884d8", "#82ca9d", "#ca8282ff"][index]}
//                 />
//               ))}
//             </BarChart>
//           </ResponsiveContainer>
//         </div>
//       ))}
//     </div>
//   );
// };

// export default ServicesChart;
